import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadQuotes from "@insite/client-framework/Store/Data/Quotes/Handlers/LoadQuotes";
import { getQuotesDataView } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardContainer, { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import CardList, { CardListStyles } from "@insite/content-library/Components/CardList";
import QuoteSummaryCard, { QuoteSummaryCardStyles } from "@insite/content-library/Components/QuoteSummaryCard";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import CardListHeading from "@rightstore/Overrides/Components/CardListHeading";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    numberOfRecords = "numberOfRecords",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        settingsCollection: getSettingsCollection(state),
        session,
        quotesDataView: getQuotesDataView(state, recentQuotesParameter),
        myQuotesPageNavLink: getPageLinkByPageType(state, "RfqMyQuotesPage"),
        canViewQuotes: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
    };
};

const mapDispatchToProps = {
    loadQuotes,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentQuotesStyles {
    cardList?: CardListStyles;
    noQuotesGridItem?: GridItemProps;
    noQuotesText?: TypographyPresentationProps;
    cardContainer?: CardContainerStyles;
    quoteSummaryCard?: QuoteSummaryCardStyles;
    allLink?: InjectableCss;
    containerGridItem?: GridItemProps;
    cardListHeading?: TypographyPresentationProps;
    PriceGridItem?: GridItemProps;
    cardListGridItem?: InjectableCss;
}

export const recentQuotesStyles: RecentQuotesStyles = {
    noQuotesGridItem: { width: 12 },
    allLink: {
        css: css`
            jusify-content: flex-end;
            padding: 15px 0 15px 15px;

            span {
                color: #006FF8!important;
                text-decoration: underline;
                font-size: 1rem;
                font-weight: 400;
            }
        `,
    },
    containerGridItem: {
        css: css`
            padding: 0;
        `,
    },
    cardListHeading: {
        size: 18,
        weight: 700,
        lineHeight: "28px",
        color: "#333333",
        css: css`
            margin-bottom: 0;
        `,
    },
    cardListGridItem: {
        css: css`     
            padding: 15px 15px 15px 0;       
        `,
    },
};

const recentQuotesParameter = {
    page: 1,
    pageSize: 3,
};

const styles = recentQuotesStyles;

class RecentQuotes extends React.Component<Props> {
    componentDidMount() {
        recentQuotesParameter.pageSize = this.props.fields.numberOfRecords;
        if (this.props.canViewQuotes && !this.props.quotesDataView.value) {
            this.props.loadQuotes(recentQuotesParameter);
        }
    }

    render() {
        const { settingsCollection, session, quotesDataView, myQuotesPageNavLink, canViewQuotes } = this.props;
        if (!canViewQuotes || !quotesDataView.value) {
            return null;
        }

        const myQuotesPageUrl = myQuotesPageNavLink ? myQuotesPageNavLink.url : undefined;

        const customCssWrapper = {
            css: css`
                ${this.props.fields.customCSS}
            `,
        };

        return (
            <StyledWrapper {...customCssWrapper}>
                <GridContainer
                    offsetCss={css`                       
                        padding-top: 24px;
                        margin: 0;
                        &&& {
                            width: 100%;
                        }
                    `}
                >
                    <GridItem width={8} {...styles.cardListGridItem}>
                        <Typography {...styles.cardListHeading} variant="h2">
                            {translate("Recent quotes")}
                        </Typography>
                    </GridItem>
                    {myQuotesPageUrl && (
                        <GridItem width={4} {...styles.allLink}>
                            <Link
                                href={myQuotesPageUrl}
                                typographyProps={{ size: 15 }}
                                css={css`
                                    margin-left: auto;
                                    margin-top: 5px;
                                `}
                                className="view-all-link"
                                data-test-selector="cardListHeadingViewAllLink"
                            >
                                {translate("View All")}
                                <VisuallyHidden>
                                    {translate("View All")} {translate("Recent purchases")}
                                </VisuallyHidden>
                            </Link>
                        </GridItem>
                    )}
                    <GridItem
                        width={12}
                    >
                        <CardList
                            css={css`
                            padding-bottom: 50px;
                            > div {
                                overflow-x: auto;     
                            }       
                        `}
                            extendedStyles={styles.cardList}
                        >
                            <CardListHeading heading={translate("Recent Quotes")} viewAllUrl={myQuotesPageUrl} headingType="Quote" />
                            {quotesDataView.value.length === 0 && (
                                <GridItem {...styles.noQuotesGridItem}>
                                    <Typography {...styles.noQuotesText}>{siteMessage("Rfq_NoQuotesMessage")}</Typography>
                                </GridItem>
                            )}
                            {quotesDataView.value.map(quote => (
                                <CardContainer key={quote.id} extendedStyles={styles.cardContainer}>
                                    <QuoteSummaryCard
                                        quote={quote}
                                        session={session}
                                        quoteSettings={settingsCollection.quoteSettings}
                                        extendedStyles={styles.quoteSummaryCard}
                                    />
                                </CardContainer>
                            ))}
                        </CardList>
                    </GridItem>
                </GridContainer>
            </StyledWrapper>
        );
    }
}

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentQuotes),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.numberOfRecords,
                displayName: "Number of Recent Quotes Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 3,
                fieldType: "General",
                sortOrder: 1,
                tab: basicTab,
            },
        ]
    },
};

export default widgetModule;
